import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloCache, ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ContractOutreachStateDocument,
  ContractOutreachStateQuery,
  ContractOutreachStateQueryVariables,
  ContractOutreachSummaryListQuery,
  useContinueContractRemindersMutation,
  useContractOutreachStateQuery,
  useContractOutreachSummaryListQuery,
  usePauseContractRemindersMutation,
  useRequestContractReminderMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import { useToasts } from '../Toast/useToasts';
import {
  OUTREACH_SLOW_POLL_MS,
  outreachFastUntil,
  outreachNextWakeAt,
  outreachPollInterval,
} from './outreachModel';
import { useEsignPermissions } from './useEsignPermissions';

export type IContractOutreach = NonNullable<
  ContractOutreachStateQuery['contractOutreach']
>;

export type IContractOutreachMessage = IContractOutreach['messages'][number];

export type IContractOutreachSummary = ContractOutreachSummaryListQuery['contractOutreachSummaries'][number];

type RawErrors =
  | Array<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

interface IOutreachPayload {
  success?: boolean | null;
  customErrors?: RawErrors;
  outreach?: IContractOutreach | null;
}

/**
 * Ответ мутации — полное состояние напоминаний (тот же фрагмент, что у
 * запроса ящика). У `ContractOutreachType` нет `id`, кэш его не
 * нормализует, поэтому ответ кладётся прямо в запрос ящика с ТЕМИ ЖЕ
 * переменными.
 *
 * Не `refetchQueries` по имени операции: так бьётся и по пропущенному
 * (`skip`) запросу (грабли `refetch-by-name-skipped-query`).
 *
 * Журнал договора (действие сотрудника бэкенд пишет туда же) здесь НЕ
 * сбрасывается через `evict` поля `events`: запрос карточки стал бы
 * неполным, ящик на время перезапроса показал бы спиннер вместо всего
 * содержимого и прокрутился бы наверх — а кнопки этой секции внизу ящика.
 * Карточку перечитывает её же запрос (`onJournalChanged` у секции).
 *
 * Сводка SMS списка договоров (колонка «SMS») — сбрасывается: иначе за
 * окном договора строка показывала бы прежнее число отправленных.
 */
export const settleOutreachCache = (
  cache: ApolloCache<unknown>,
  contractId: string,
  payload: IOutreachPayload | null | undefined,
) => {
  if (!payload?.success || !payload.outreach) return;
  cache.evict({ id: 'ROOT_QUERY', fieldName: 'contractOutreachSummaries' });
  cache.writeQuery<
    ContractOutreachStateQuery,
    ContractOutreachStateQueryVariables
  >({
    query: ContractOutreachStateDocument,
    variables: { contractId },
    data: { __typename: 'Query', contractOutreach: payload.outreach },
  });
};

export interface IUseContractOutreach {
  outreach: IContractOutreach | null;
  loading: boolean;
  error?: ApolloError;
  sendNow: () => Promise<boolean>;
  stop: () => Promise<boolean>;
  resume: () => Promise<boolean>;
  busy: {
    sending: boolean;
    stopping: boolean;
    resuming: boolean;
  };
}

/**
 * ТЗ 0077 — SMS-напоминания одного договора студента (секция ящика).
 *
 * `enabled=false` — секцию показывать не для чего (черновик, договор фирмы
 * или преподавателя): запрос не уходит вовсе. Без права читать договоры
 * студентов тоже не уходит — отказ прав отклонил бы промис поверх ящика.
 *
 * Опрос раз в 5 с — только пока есть сообщение «в пути» (отправляется или
 * ждёт в очереди без переноса): «Напомнить сейчас» ставит SMS в очередь, и
 * без опроса человек смотрел бы на «В очереди» до перезагрузки.
 *
 * Отказы — как в `useEsignMutations`: бизнес-ошибка приходит в
 * `customErrors` (код → `esign.outreach.errors.<CODE>`, неизвестный — текстом
 * бэкенда), отказ прав отклоняет промис. Тост — внутри хука.
 */
export const useContractOutreach = (
  contractId: string | null,
  enabled = true,
): IUseContractOutreach => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const { canReadStudent } = useEsignPermissions();
  const skip = !contractId || !enabled || !canReadStudent;

  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useContractOutreachStateQuery({
    variables: { contractId: contractId || '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const outreach = data?.contractOutreach ?? null;
  const messages = outreach?.messages;
  const pollMs = messages ? outreachPollInterval(messages) : 0;
  usePageVisiblePoll(
    startPolling,
    stopPolling,
    pollMs || OUTREACH_SLOW_POLL_MS,
    !skip && pollMs > 0,
  );

  // Частый опрос кончается по времени, а не по данным: к концу окна
  // перерисовываемся сами, чтобы интервал пересчитался и стал редким.
  const [, setTick] = useState(0);
  const fastUntil = messages ? outreachFastUntil(messages) : null;
  useEffect(() => {
    if (skip || fastUntil === null) return undefined;
    const timer = window.setTimeout(
      () => setTick((value) => value + 1),
      Math.min(Math.max(fastUntil - Date.now() + 1000, 0), 2 ** 31 - 1),
    );
    return () => window.clearTimeout(timer);
  }, [skip, fastUntil]);

  // Сообщение перенесено на утро (тихие часы, суточный лимит): в этот
  // момент перечитать самим — ход времени ничего не перерисовывает, и
  // «в очереди» висело бы до перезагрузки ящика.
  const wakeAt = messages ? outreachNextWakeAt(messages) : null;
  useEffect(() => {
    if (skip || wakeAt === null) return undefined;
    const delay = Math.min(wakeAt - Date.now() + 5000, 2 ** 31 - 1);
    const timer = window.setTimeout(() => {
      refetch().catch(() => undefined);
    }, Math.max(delay, 0));
    return () => window.clearTimeout(timer);
  }, [skip, wakeAt, refetch]);

  const [
    requestReminder,
    { loading: sending },
  ] = useRequestContractReminderMutation();
  const [
    pauseReminders,
    { loading: stopping },
  ] = usePauseContractRemindersMutation();
  const [
    continueReminders,
    { loading: resuming },
  ] = useContinueContractRemindersMutation();

  // Защёлка в ref, а не в state: второй клик в том же такте ещё видит
  // `loading=false` и поставил бы второе SMS (бэкенд ответил бы COOLDOWN,
  // но человек получил бы тост об ошибке после успешного действия).
  const inFlightRef = useRef(false);

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) => {
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Esign,
      });
    },
    [addToast],
  );

  const errorText = useCallback(
    (raw: RawErrors): string =>
      (raw ?? [])
        .filter((item): item is NonNullable<typeof item> => Boolean(item))
        .map((item) =>
          item.code
            ? t(`esign.outreach.errors.${item.code}`, {
                defaultValue: item.message || item.code,
              })
            : item.message ?? '',
        )
        .filter(Boolean)
        .join('\n'),
    [t],
  );

  const run = useCallback(
    async (
      action: () => Promise<IOutreachPayload | null | undefined>,
      successKey: string,
    ): Promise<boolean> => {
      if (!contractId || inFlightRef.current) return false;
      inFlightRef.current = true;
      try {
        const payload = await action();
        if (payload?.success) {
          notify(ToastTypes.success, t(successKey));
          return true;
        }
        notify(
          ToastTypes.error,
          t('esign.outreach.toast.failed'),
          errorText(payload?.customErrors),
        );
        return false;
      } catch (caught) {
        // Отказ прав или сеть — не сырой текст сервера (в польском
        // интерфейсе он был бы русским), а свой перевод.
        const noAccess =
          caught instanceof ApolloError && caught.graphQLErrors.length > 0;
        notify(
          ToastTypes.error,
          t('esign.outreach.toast.failed'),
          t(
            noAccess
              ? 'esign.outreach.errors.NO_ACCESS'
              : 'esign.outreach.errors.NETWORK',
          ),
        );
        return false;
      } finally {
        inFlightRef.current = false;
      }
    },
    [contractId, errorText, notify, t],
  );

  const sendNow = useCallback(
    () =>
      run(async () => {
        const id = contractId || '';
        const result = await requestReminder({
          variables: { contractId: id },
          update: (cache, { data: answer }) =>
            settleOutreachCache(cache, id, answer?.sendContractReminderNow),
        });
        return result.data?.sendContractReminderNow;
      }, 'esign.outreach.toast.requested'),
    [contractId, requestReminder, run],
  );

  const stop = useCallback(
    () =>
      run(async () => {
        const id = contractId || '';
        const result = await pauseReminders({
          variables: { contractId: id },
          update: (cache, { data: answer }) =>
            settleOutreachCache(cache, id, answer?.stopContractReminders),
        });
        return result.data?.stopContractReminders;
      }, 'esign.outreach.toast.stopped'),
    [contractId, pauseReminders, run],
  );

  const resume = useCallback(
    () =>
      run(async () => {
        const id = contractId || '';
        const result = await continueReminders({
          variables: { contractId: id },
          update: (cache, { data: answer }) =>
            settleOutreachCache(cache, id, answer?.resumeContractReminders),
        });
        return result.data?.resumeContractReminders;
      }, 'esign.outreach.toast.resumed'),
    [contractId, continueReminders, run],
  );

  return {
    outreach,
    loading,
    error,
    sendNow,
    stop,
    resume,
    busy: { sending, stopping, resuming },
  };
};

const EMPTY_SUMMARIES: Readonly<Record<string, IContractOutreachSummary>> = {};

/**
 * Сводка SMS для строк списка договоров: ОДИН запрос на страницу.
 *
 * Пустой список id — запроса нет. Пока ответ на новые id не пришёл, держим
 * прошлую выдачу (`previousData`): строки, что остались на экране, не мигают
 * прочерком, таблица не перерисовывается пустой.
 *
 * Ключ результата — id договора строкой (`contractId` бэкенд отдаёт pk).
 */
export const useContractOutreachSummaries = (
  contractIds: string[],
): Readonly<Record<string, IContractOutreachSummary>> => {
  const { canReadStudent } = useEsignPermissions();
  const idsKey = contractIds.join(',');
  const variables = useMemo(
    () => ({ contractIds: idsKey ? idsKey.split(',') : [] }),
    [idsKey],
  );
  const skip = !canReadStudent || variables.contractIds.length === 0;

  const {
    data,
    previousData,
    startPolling,
    stopPolling,
  } = useContractOutreachSummaryListQuery({
    variables,
    skip,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });
  // SMS уходят роботом и из ящика договора — колонка раз в минуту
  // перечитывается сама, пока вкладка видна.
  usePageVisiblePoll(startPolling, stopPolling, OUTREACH_SLOW_POLL_MS, !skip);

  const rows = (data ?? previousData)?.contractOutreachSummaries;
  return useMemo(() => {
    if (!rows || rows.length === 0) return EMPTY_SUMMARIES;
    return rows.reduce<Record<string, IContractOutreachSummary>>((acc, row) => {
      acc[String(row.contractId)] = row;
      return acc;
    }, {});
  }, [rows]);
};
